import { type FC, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'

import { type ClientSessionCustomerData } from '@redteclab/api/clients/bully'

import { useGlobalConfigContext } from '../../global-config'
import { DEPLOYMENT_STAGE } from '../../process-env'
import { queryStringEncode } from '../../url-handling'
import { useUserApiSwrSessionV1Customer } from '../../user-data'

import styles from './CallSchedulingToolIframe.module.css'

type MessageType = MessageEvent<{ data: { height: number }; event: string }>

const resizeIframe = (
  message: MessageType,
  iframe: HTMLIFrameElement | null,
): void => {
  if (iframe && message.data.event === 'embed-resize') {
    const maxHeight = Math.max(iframe.offsetHeight, message.data.data.height)
    iframe.style.height = `${maxHeight}px`
  }
}

const getPrefilledQueryStringData = (
  userData: ClientSessionCustomerData | undefined,
): string => {
  if (!userData?.customerNumber) {
    return ''
  }

  return queryStringEncode({
    customerId: userData.customerNumber,
    email: userData.email,
    firstname: userData.firstName,
    lastname: userData.lastName,
    phone: userData.phone,
  })
}

const getMeetergoUrl = (
  deploymentStage: `${DEPLOYMENT_STAGE}`,
  prefilledUserData: string,
): string =>
  deploymentStage === DEPLOYMENT_STAGE.PRODUCTION
    ? `https://my.meetergo.com/form/61e023ec-1aa7-4a0e-9310-a6abec942e52${prefilledUserData}`
    : `https://my.meetergo.com/form/b6ae53d7-c469-46d6-a55c-e27f7dfcbb5a${prefilledUserData}`

export const CallSchedulingToolIframe: FC<{ onIframeLoad?: () => void }> = ({
  onIframeLoad,
}) => {
  const { deploymentStage } = useGlobalConfigContext()
  const refIframe = useRef(null)
  const intl = useIntl()
  const { data: userData } = useUserApiSwrSessionV1Customer()

  const prefilledUserData = getPrefilledQueryStringData(userData)
  useEffect(() => {
    /*
     *This is necessary in order to adjust the iFrame height without implementing any external script.
     *Reference: https://github.com/meetergo/meetergo-integration/blob/3f09c4ca6b59f40c4199432e2df38bb5e500bde0/src/main.ts#L185
     *We are reading a PostMessage with the scrollHeight of the iframe content.
     *We could not access the property iframe.contentWindow.document.body.scrollHeight directly because
     *it's in a different domain.
     *If we were to implement their script, this function would not be needed, but we'd need to extend
     *the window interface and declare inside it enableAutoResize: true.
     */
    window.addEventListener('message', (message: MessageType) => {
      if (message.origin !== 'https://my.meetergo.com') {
        return
      }
      resizeIframe(message, refIframe.current)
    })
  }, [])

  return (
    // eslint-disable-next-line react/iframe-missing-sandbox -- the rule was disabled before a comment became required
    <iframe
      className={styles['meetergo-container']}
      onLoad={() => {
        if (onIframeLoad) {
          onIframeLoad()
        }
      }}
      ref={refIframe}
      src={getMeetergoUrl(deploymentStage, prefilledUserData)}
      title={intl.formatMessage({ id: 'contact.callSchedulingTool.title' })}
    />
  )
}
